/**
 * 误排工作安排的删除入口：经理在任务详情明确确认后删除单条安排。
 * 后端软删除并留档，界面说明项目、合同和其他任务保留；失败时保留确认框便于核对。
 */
import React, { useRef, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

/** task 为当前可编辑任务及其版本；删除期间锁住父详情的报工、改期等操作。 */
export default function ScheduleDeleteDialog({ task, api, zh, disabled, onBusyChange, onDeleted }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(false);
  const submitting = useRef(false);
  const cancelRef = useRef(null);
  if (!task.canEdit) return null;

  const remove = async () => {
    if (submitting.current || disabled || conflict) return;
    submitting.current = true; setBusy(true); onBusyChange(true); setError("");
    try {
      await api.delete(`/subcontractor-schedule/tasks/${encodeURIComponent(task.id)}`, { data: { version: task.version } });
      setOpen(false); onDeleted(task.id);
    } catch (e) {
      // 有人在确认期间报工或改期时，旧版本不得删除；要求重新查看详情后再作决定。
      const stale = e?.response?.status === 409;
      setConflict(stale);
      const detail = e?.response?.data?.detail;
      setError(stale ? (zh ? "工作安排已更新，请关闭详情并重新打开，核对后再删除。" : "This assignment changed. Close and reopen its details before deleting.")
        : (typeof detail === "string" ? detail : (zh ? "删除失败，请稍后重试。" : "Could not delete. Please try again.")));
    } finally {
      submitting.current = false; setBusy(false); onBusyChange(false);
    }
  };

  return <>
    <Button variant="outline" className="min-h-11 text-red-700 hover:bg-red-50" disabled={disabled} data-testid="schedule-delete-open"
      onClick={() => { setError(""); setConflict(false); setOpen(true); }}>
      <Trash2 aria-hidden="true" className="mr-1 h-4 w-4" />{zh ? "删除工作安排" : "Delete assignment"}
    </Button>
    <Dialog open={open} onOpenChange={(value) => { if (!submitting.current) setOpen(value); }}>
      <DialogContent className="w-[calc(100%_-_2rem)] sm:max-w-md z-[10003]" overlayClassName="z-[10002]"
        onOpenAutoFocus={(event) => { event.preventDefault(); cancelRef.current?.focus(); }}>
        <DialogHeader>
          <DialogTitle>{zh ? "删除这条工作安排？" : "Delete this assignment?"}</DialogTitle>
          <DialogDescription>{zh ? "删除后，分包商月历和工人页面将不再显示这条安排。项目、合同和其他任务保留。" : "This assignment will disappear from the subcontractor calendar and worker page. The project, contract and other tasks remain."}</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-slate-50 p-3 text-sm break-words" data-testid="schedule-delete-summary">
          <p className="font-semibold">{task.title}</p><p className="mt-1 text-slate-600">{task.subcontractorName} · {task.address}</p>
          <p className="mt-1 text-slate-600">{task.startAt.replace("T", " ")} → {task.endAt.replace("T", " ")}</p>
        </div>
        <p className="text-sm text-slate-600">{zh ? "已有文字、照片、视频及进度记录留档；待处理的改期申请会一并关闭。删除后可以重新安排。" : "Existing notes, photos, videos and progress records are retained. Pending reschedule requests will close. You can create a new assignment afterward."}</p>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button ref={cancelRef} variant="outline" className="min-h-11" disabled={busy} data-testid="schedule-delete-cancel" onClick={() => setOpen(false)}>{zh ? "保留安排" : "Keep assignment"}</Button>
          <Button variant="destructive" className="min-h-11" disabled={busy || disabled || conflict} data-testid="schedule-delete-confirm" onClick={remove}>
            {busy && <Loader2 aria-hidden="true" className="mr-1 h-4 w-4 animate-spin" />}{zh ? "确认删除" : "Confirm deletion"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  </>;
}
